import React, { useState, useEffect } from "react";
import { Auth, CognitoUser } from "@aws-amplify/auth";
import {
  EuiCard,
  EuiButton,
  EuiSpacer,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiLoadingSpinner,
  EuiIcon,
  EuiPageTemplate,
  EuiPanel,
  EuiBadge,
  EuiButtonEmpty,
  EuiFieldText,
  EuiFormRow,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
} from "@inscopix/ideas-eui";
import { MFASetupForm } from "./MFASetupForm";
import { captureException } from "@sentry/react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";

export const MFAManagement = () => {
  const [mfaEnabled, setMfaEnabled] = useState<boolean>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSetup, setShowSetup] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [isVerifyingDisable, setIsVerifyingDisable] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);

  useEffect(() => {
    void loadMFAStatus();
  }, []);

  const loadMFAStatus = async () => {
    try {
      setIsLoading(true);
      const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;

      // Get preferred MFA
      let preferredMfa: string | null = null;
      try {
        preferredMfa = await Auth.getPreferredMFA(user);
      } catch (err) {
        // No MFA preference set yet
        preferredMfa = null;
      }

      // Since TOTP is the only enabled MFA type, check if it's preferred
      const isEnabled = preferredMfa !== null && preferredMfa !== "NOMFA";
      const isTOTPPreferred = preferredMfa === "SOFTWARE_TOKEN_MFA";
      if (isEnabled && !isTOTPPreferred) {
        throw new Error(
          `Unexpected MFA state: MFA is enabled but TOTP not preferred, instead got ${preferredMfa !== null ? preferredMfa : "null"}`,
        );
      }

      setMfaEnabled(isEnabled);
      setError(null);
    } catch (error) {
      captureException(error);
      setError("Failed to load MFA settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableTOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setDisableError(null);

    if (disableCode.length !== 6) {
      setDisableError("Code must be 6 digits");
      return;
    }

    try {
      setIsVerifyingDisable(true);
      const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;

      await Auth.verifyTotpToken(user, disableCode);
      // Disable TOTP by setting preferred to NOMFA
      await Auth.setPreferredMFA(user, "NOMFA");

      setShowConfirmDisable(false);
      await loadMFAStatus();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to disable TOTP";
      setDisableError(errorMessage);
      captureException(err);
      addUtilityToastFailure("Could not disable MFA for your account");
    } finally {
      setIsVerifyingDisable(false);
    }
  };

  if (isLoading && !mfaEnabled) {
    return (
      <EuiPageTemplate.Section>
        <EuiFlexGroup justifyContent="center">
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size="l" />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiPageTemplate.Section>
    );
  }

  const confirmDisableModal = (
    <EuiModal onClose={() => setShowConfirmDisable(false)}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          Disable Multi-Factor Authentication
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiText>
          <p>
            Are you sure you want to disable Multi-Factor Authentication? This
            will remove the extra layer of security from your account.
          </p>
          <p>
            Enter the 6‑digit code from your authenticator app to confirm that
            you want to disable MFA on this account.
          </p>
        </EuiText>
        <EuiSpacer size="m" />
        <form onSubmit={(event) => void handleDisableTOTP(event)}>
          <EuiFormRow label="Authenticator code">
            <EuiFieldText
              value={disableCode}
              onChange={(e) =>
                setDisableCode(e.target.value.replace(/\D/g, ""))
              }
              inputMode="numeric"
              maxLength={6}
              pattern="[0-9]{6}"
              required
            />
          </EuiFormRow>
          {disableError != null && (
            <>
              <EuiSpacer size="s" />
              <EuiCallOut title="Error" color="danger" iconType="alert">
                {disableError}
              </EuiCallOut>
            </>
          )}
          <EuiSpacer size="m" />
          <EuiFlexGroup justifyContent="flexEnd" gutterSize="s">
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty onClick={() => setShowConfirmDisable(false)}>
                Cancel
              </EuiButtonEmpty>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton
                type="submit"
                isLoading={isVerifyingDisable}
                disabled={disableCode.length !== 6}
                fill
                color="danger"
              >
                Verify & disable
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </form>
      </EuiModalBody>
    </EuiModal>
  );

  return (
    <>
      {error != null && (
        <>
          <EuiCallOut
            title="Error"
            color="danger"
            iconType="alert"
            onDismiss={() => setError(null)}
          >
            {error}
          </EuiCallOut>
          <EuiSpacer size="m" />
        </>
      )}
      {showSetup ? (
        <EuiPanel>
          <MFASetupForm
            onSuccess={() => {
              setShowSetup(false);
              void loadMFAStatus();
            }}
            onCancel={() => {
              setShowSetup(false);
            }}
          />
        </EuiPanel>
      ) : (
        <EuiCard
          icon={<EuiIcon type="lock" size="xl" />}
          title="Multi-Factor Authentication (TOTP)"
          description={
            mfaEnabled ? (
              <EuiBadge color="success">Active</EuiBadge>
            ) : (
              <EuiBadge color="hollow">Not configured</EuiBadge>
            )
          }
          footer={
            <EuiFlexGroup gutterSize="s" justifyContent="spaceAround">
              {!mfaEnabled ? (
                <EuiFlexItem grow={false}>
                  <EuiButton
                    onClick={() => setShowSetup(true)}
                    isDisabled={isLoading}
                    fill
                  >
                    Set Up Multi-Factor Authentication
                  </EuiButton>
                </EuiFlexItem>
              ) : (
                <EuiFlexItem grow={false}>
                  <EuiButton
                    onClick={() => {
                      setDisableCode("");
                      setDisableError(null);
                      setShowConfirmDisable(true);
                    }}
                    isDisabled={isVerifyingDisable}
                    color="text"
                  >
                    Disable MFA
                  </EuiButton>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          }
        >
          <EuiFlexGroup alignItems="center" gutterSize="m">
            <EuiFlexItem>
              <EuiText size="s" color="subdued">
                {mfaEnabled
                  ? "Your account is protected with two-factor authentication using an authenticator app."
                  : "Enable two-factor authentication to add an extra layer of security to your account."}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiCard>
      )}
      {showConfirmDisable && confirmDisableModal}
    </>
  );
};
